import type { UnifiedFeeItem } from '../types';

export const CARD_TYPES = [
	'Credit',
	'Debit',
	'PIN debit',
	'Amex',
	'EBT',
	'Unclassified',
] as const;

export type CardType = (typeof CARD_TYPES)[number];

export interface InterchangeLine {
	description: string;
	cardType: CardType;
	downgrade: boolean;
	count: number | null;
	volume: number;
	fees: number;
}

export interface CardTypeTotal {
	cardType: CardType;
	count: number | null;
	volume: number;
	fees: number;
}

export interface InterchangeBreakdown {
	lines: InterchangeLine[];
	totals: CardTypeTotal[];
	downgradeFees: number;
	totalFees: number;
	totalVolume: number;
}

// Processors abbreviate differently (VS/VI/VISA, DB/DBT/D, PP/PPD...), so these match whole tokens.
const PIN =
	/\bPIN\b|PIN_DEBIT|\b(INTERLINK|MAESTRO|PULSE|NYCE|STAR|ACCEL|SWITCH|SHAZAM|JEANIE)\b/;
const AMEX = /\b(AMEX|AXP?|AOB|AM|AE)\b|AMERICAN EXPRESS/;
const CREDIT = /\b(CR|CREDIT)\b/;
// Regulated debit (REG, DRF, D R) is debit too until extraction tags it.
const DEBIT =
	/\b(DBT|DB|DEBIT|D|DR|DRF|PP|PPD|PREPAID|PRPD)\b|\bREG(ULATED)?\b/;
// Fused names like STDCONSSIG and DISCOVER MIDQUAL are downgrades; NON PREMIUM and NON REGULATED are not downgrades.
const DOWNGRADE =
	/NONQUAL|NON-QUAL|MIDQUAL|MID-QUAL|EIRF|KEYENTRY|KEYED|STD|\bNON\b(?![ -]?(PREM|REG))|\bMID\b|\bSTANDARD\b/;

const NETWORK_CARD_TYPES: Record<string, CardType> = {
	pin_debit: 'PIN debit',
	amex: 'Amex',
	ebt: 'EBT',
};

export const classifyCardType = (
	description: string,
	cardNetwork?: string,
): CardType => {
	const network = (cardNetwork ?? '').toLowerCase();
	const fromNetwork = NETWORK_CARD_TYPES[network];
	if (fromNetwork) return fromNetwork;
	const d = description.toUpperCase().replace(/^CR-/, '');
	// Short PIN and Amex tokens (STAR, AM, PIN) also appear in Visa/MC names, so use them only without a network.
	const nameDecidesNetwork = !network || network === 'unknown';
	if (nameDecidesNetwork && PIN.test(d)) return 'PIN debit';
	if (nameDecidesNetwork && AMEX.test(d)) return 'Amex';
	if (CREDIT.test(d)) return 'Credit';
	if (DEBIT.test(d)) return 'Debit';
	return 'Unclassified';
};

// The base PIN debit rate is often labelled "Standard", so PIN and EBT are never downgrades.
export const isDowngrade = (description: string, cardType: CardType): boolean =>
	cardType !== 'PIN debit' &&
	cardType !== 'EBT' &&
	DOWNGRADE.test(description.toUpperCase());

// Synthetic flat-rate estimates, estimated rows (fee zeroed) and processor tier rates are not interchange.
const isInterchange = (item: UnifiedFeeItem): boolean => {
	const extra = item.extra_attributes ?? {};
	return !(
		item.is_estimated ||
		extra.synthetic ||
		extra.comparison_only ||
		extra.fee_type === 'tiered_rate'
	);
};

// SWITCH rows repeat the transactions of the PIN network rows (SWITCH-MAESTRO = MAESTRO), so they add fees only.
const isSwitchFee = (description: string): boolean =>
	/^SWITCH\b/i.test(description.trim());

// A parent row is the sum of its children, so count only rows without children.
const leaves = (items: UnifiedFeeItem[]): UnifiedFeeItem[] =>
	items.flatMap((item) =>
		item.children?.length ? leaves(item.children) : [item],
	);

// null when no row in the group printed a count.
const sumCounts = (lines: InterchangeLine[]): number | null =>
	lines.some((line) => line.count !== null)
		? lines.reduce((sum, line) => sum + (line.count ?? 0), 0)
		: null;

export const buildInterchangeBreakdown = (
	items: UnifiedFeeItem[],
): InterchangeBreakdown => {
	const lines = leaves(items)
		.filter(isInterchange)
		.map((item) => {
			const cardType = classifyCardType(
				item.type,
				item.extra_attributes?.card_network,
			);
			const switchFee = isSwitchFee(item.type);
			return {
				description: item.type,
				cardType,
				downgrade: isDowngrade(item.type, cardType),
				count: switchFee ? null : Number(item.ic.count) || null,
				volume: switchFee ? 0 : Number(item.ic.volume) || 0,
				fees: Number(item.ic.feeDue) || 0,
			};
		});

	const totals = CARD_TYPES.map((cardType) => {
		const group = lines.filter((line) => line.cardType === cardType);
		return {
			cardType,
			count: sumCounts(group),
			volume: group.reduce((sum, line) => sum + line.volume, 0),
			fees: group.reduce((sum, line) => sum + line.fees, 0),
		};
	}).filter((total) => total.volume || total.fees);

	return {
		lines,
		totals,
		downgradeFees: lines
			.filter((line) => line.downgrade)
			.reduce((sum, line) => sum + line.fees, 0),
		totalFees: lines.reduce((sum, line) => sum + line.fees, 0),
		totalVolume: lines.reduce((sum, line) => sum + line.volume, 0),
	};
};

export const effectiveRate = (fees: number, volume: number): string =>
	volume > 0 ? `${((fees / volume) * 100).toFixed(2)}%` : '—';
